import { html, css, nothing } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { Debouncer } from '../../../../../ltn-helpers/utils/debouncer.js';
import { timeOut } from '../../../../../ltn-helpers/utils/async.js';

import JobSchema from '../../../../../models/job-schema.js';

import NSJobComponent from '../../../job.js';

import Company from '../../../../../models/company.js';
import '../../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/node-cash/select-company.json';
import { jobSchema } from '../../../job-schema.js';

@customElement(NSSelectCompanyToTransferNodeCash.is)
export default class NSSelectCompanyToTransferNodeCash extends NSJobComponent {
  static get is() {
    return 'ns-select-company-to-transfer-node-cash';
  }

  private __debounceSearchInput?: Debouncer;

  @state()
  private __companyId?: string;

  @state()
  private __searchNoResults: boolean = false;

  @state()
  private __searchResults: Company[] = [];

  @state()
  private __searchStatus: string = '';

  @query('#company-search')
  private __searchInput?: HTMLInputElement;

  static styles = [
    ...super.styles,
    css`
      .job-option.selected {
        background: var(--ns-theme-green);
        color: var(--ns-theme-black);
      }
    `,
  ];

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'button', label: 'Complete' } } });
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;

    if (!this._db) throw new Error('Unable to find db service');
  }

  private __companySearch() {
    const val = this.__searchInput?.value;

    if (val && val.length > 2) {
      this.__debounceSearchInput = Debouncer.debounce(this.__debounceSearchInput, timeOut.after(10), async () => {
        if (!this._db) throw new Error('Unable to find db service');

        this.__searchStatus = 'Searching for companies...';

        const query = await this._db.query(
          'company',
          {
            name: {
              $rexi: val,
            },
          },
          {
            limit: 10,
          },
        );

        if (!query) throw new Error('Failed to query companies');

        if (query.results.length === 0) {
          this.__searchNoResults = true;
          this.__searchStatus = "We couldn't find any companies in the system using that search term.";
          return;
        }

        this.__searchNoResults = false;
        const companies = query.results as Company[];
        this.__searchResults = companies;

        this.__searchStatus = 'Found Companies';
      });

      this.__searchStatus = '';
    }
  }

  async _outcomeCompleteHandler() {
    if (!this.__companyId) throw new Error('Unable to complete job without selecting a company');
    this.outcome('complete', { recipientCompanyId: this.__companyId });
  }

  render() {
    return html`
      <ns-job-layout>
        <div class="job-panel">
          <p>Search for a company using the field below.</p>
          <wa-input
            class="w-100"
            required
            id="company-search"
            type="text"
            placeholder="Company Name"
            label="Company name"
          ></wa-input>
          <wa-button variant="brand" type="button" @click=${this.__companySearch}>Search</wa-button>
        </div>
        <div class="job-panel">
          <p>Companies which you can select in the system will appear here.</p>
          ${this.__searchStatus
            ? html`
                <p>${this.__searchStatus}</p>
              `
            : nothing}
          ${!this.__searchNoResults
            ? repeat(
                this.__searchResults,
                (company) => html`
                  <div class="job-option ${company.id === this.__companyId ? 'selected' : ''}">
                    <span class="flex">${company.name}</span>
                    <wa-button variant="brand" size="s" type="button" @click="${() => (this.__companyId = company.id)}">
                      select
                    </wa-button>
                  </div>
                `,
              )
            : nothing}
        </div>
      </ns-job-layout>
    `;
  }
}
